import { calculateHeaderGroupHeight } from '@theme/critical';
import { Component } from '@theme/component';
import { onDocumentLoaded, changeMetaThemeColor } from '@theme/utilities';

class HeaderComponent extends Component {
  requiredRefs = ['headerDrawerContainer', 'headerMenu', 'headerRowTop'];

  #menuDrawerHiddenWidth = null;

  #intersectionObserver = null;

  #offscreen = false;

  #lastScrollTop = 0;

  #timeout = null;

  #headerHeightTimeout = null;

  #animationDelay = 150;

  #scrollThreshold = 5;

  #throttledScrollHandler = null;

  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry) return;

    const { height } = entry.target.getBoundingClientRect();

    if (this.#headerHeightTimeout) {
      clearTimeout(this.#headerHeightTimeout);
    }

    this.#headerHeightTimeout = setTimeout(() => {
      document.body.style.setProperty('--header-height', `${height}px`);
      this.#headerHeightTimeout = null;
    }, 100);

    if (this.#menuDrawerHiddenWidth && window.innerWidth > this.#menuDrawerHiddenWidth) {
      this.#updateMenuVisibility(false);
    }
  });

  #observeStickyPosition = (alwaysSticky = true) => {
    if (this.#intersectionObserver) return;

    const config = {
      threshold: alwaysSticky ? 1 : 0,
    };

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;

      const { isIntersecting } = entry;

      if (alwaysSticky) {
        this.dataset.stickyState = isIntersecting ? 'inactive' : 'active';
        changeMetaThemeColor(this.refs.headerRowTop);
      } else {
        this.#offscreen = !isIntersecting;
      }
    }, config);

    this.#intersectionObserver.observe(this);
  };

  #handleOverflowMinimum = (event) => {
    this.#updateMenuVisibility(event.detail.minimumReached);
  };

  #updateMenuVisibility(hideMenu) {
    if (hideMenu) {
      this.refs.headerDrawerContainer.classList.remove('desktop:hidden');
      this.#menuDrawerHiddenWidth = window.innerWidth;
      this.refs.headerMenu.classList.add('hidden');
    } else {
      this.refs.headerDrawerContainer.classList.add('desktop:hidden');
      this.#menuDrawerHiddenWidth = null;
      this.refs.headerMenu.classList.remove('hidden');
    }
  }

  #handleWindowScroll = () => {
    const stickyMode = this.getAttribute('sticky');

    if (!stickyMode) {
      return;
    }

    const scrollTop = Math.max(
      window.pageYOffset || 0,
      document.documentElement.scrollTop || 0,
      document.body.scrollTop || 0,
    );
    const scrollDelta = Math.abs(scrollTop - this.#lastScrollTop);

    if (scrollDelta < this.#scrollThreshold && scrollTop > 0) {
      return;
    }

    const isScrollingUp = scrollTop < this.#lastScrollTop;

    if (this.#timeout) {
      clearTimeout(this.#timeout);
      this.#timeout = null;
    }

    if (stickyMode === 'always') {
      const isAtTop = this.getBoundingClientRect().top >= 0;

      if (isAtTop) {
        this.dataset.scrollDirection = 'none';
      } else if (isScrollingUp) {
        this.dataset.scrollDirection = 'up';
      } else {
        this.dataset.scrollDirection = 'down';
      }

      this.#lastScrollTop = scrollTop;
      return;
    }

    if (stickyMode === 'scroll-up') {
      const headerHeight =
        this.getBoundingClientRect().height ||
        parseFloat(getComputedStyle(document.body).getPropertyValue('--header-height')) ||
        0;

      if (scrollTop <= headerHeight) {
        this.removeAttribute('data-animating');
        this.#offscreen = false;
        this.dataset.stickyState = 'inactive';
        this.dataset.scrollDirection = scrollTop <= 0 ? 'none' : isScrollingUp ? 'up' : 'down';
      } else {
        if (isScrollingUp) {
          this.removeAttribute('data-animating');
          this.dataset.stickyState = 'active';
          this.dataset.scrollDirection = 'up';
        } else {
          this.removeAttribute('data-animating');
          this.dataset.stickyState = 'idle';
          this.dataset.scrollDirection = 'down';
        }
      }
    }

    this.#lastScrollTop = scrollTop;
  };

  connectedCallback() {
    super.connectedCallback();
    this.#resizeObserver.observe(this);
    this.addEventListener('overflowMinimum', this.#handleOverflowMinimum);

    const stickyMode = this.getAttribute('sticky');

    if (stickyMode) {
      this.#observeStickyPosition(stickyMode === 'always');

      if (stickyMode === 'scroll-up' || stickyMode === 'always') {
        this.#lastScrollTop = Math.max(
          window.pageYOffset || 0,
          document.documentElement.scrollTop || 0,
          document.body.scrollTop || 0,
        );

        if (stickyMode === 'scroll-up' && !this.dataset.stickyState) {
          const initialState = this.#lastScrollTop === 0 ? 'inactive' : 'active';
          this.dataset.stickyState = initialState;
        }

        const scrollHandler = this.#handleWindowScroll.bind(this);
        this.#throttledScrollHandler = scrollHandler;

        window.addEventListener('scroll', scrollHandler, { passive: true, capture: true });
        document.addEventListener('scroll', scrollHandler, { passive: true, capture: true });
        document.documentElement.addEventListener('scroll', scrollHandler, {
          passive: true,
          capture: true,
        });
        document.body.addEventListener('scroll', scrollHandler, { passive: true, capture: true });
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.#intersectionObserver?.disconnect();
    this.removeEventListener('overflowMinimum', this.#handleOverflowMinimum);
    if (this.#throttledScrollHandler) {
      const scrollHandler = this.#throttledScrollHandler;
      window.removeEventListener('scroll', scrollHandler, { capture: true });
      document.removeEventListener('scroll', scrollHandler, { capture: true });
      document.documentElement.removeEventListener('scroll', scrollHandler, { capture: true });
      document.body.removeEventListener('scroll', scrollHandler, { capture: true });
      this.#throttledScrollHandler = null;
    }
    if (this.#timeout) {
      clearTimeout(this.#timeout);
      this.#timeout = null;
    }
    if (this.#headerHeightTimeout) {
      clearTimeout(this.#headerHeightTimeout);
      this.#headerHeightTimeout = null;
    }
    document.body.style.setProperty('--header-height', '0px');
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}

onDocumentLoaded(() => {
  const header = document.querySelector('#header-component');
  const headerGroup = document.querySelector('#header-group');

  if (headerGroup) {
    const resizeObserver = new ResizeObserver(() => calculateHeaderGroupHeight(header, headerGroup));

    const children = headerGroup.children;
    for (let i = 0; i < children.length; i++) {
      const element = children[i];
      if (element === header || !(element instanceof HTMLElement)) continue;
      resizeObserver.observe(element);
    }

    const mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          const children = headerGroup.children;
          for (let i = 0; i < children.length; i++) {
            const element = children[i];
            if (element === header || !(element instanceof HTMLElement)) continue;
            resizeObserver.observe(element);
          }
        }
      }
    });

    mutationObserver.observe(headerGroup, { childList: true });
  }
});
